import React, { useState, useEffect, useRef } from 'react';
import { hashCardId } from '../../../util/hashUtils';
import { getPreloadedImage } from '../../../util/imagePreloader';
import { toast } from 'react-toastify';

// IMPORT MUI COMPONENTS
import Grid from '@mui/material/Unstable_Grid2';
import {
  Button,
  TextField,
  Alert,
  Typography,
  Box,
  Dialog,
  DialogContent,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  faArrowLeft,
  faArrowRight,
  faKey,
  faCreditCard,
  faCheck,
  faTimes,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloseIcon from '@mui/icons-material/Close';
import FunZoneLogo from './FunZoneLogo';
import useCardScanner from '../useCardScanner.jsx';

// Image path constant
import funcard from '../../../assets/funcard.webp';

function CardCheck({
  // Core props
  isOpen = true,
  onClose,
  onCancel,

  // RFID handling
  onRfidSuccess,
  onRfidError,
  intermediateTokens = {},
  eligibleUsers = [],

  // Password handling
  onPasswordSuccess,
  onPasswordError,

  // UI customization
  title = 'Mitarbeiterausweis-Login',
  description = '',
  cardText = 'Bitte Mitarbeiterausweis an das Lesegerät halten',
  showPasswordLogin = false,
  setShowPasswordLogin,
  showNoEligibleUsersMessage = true,
  disableButtonsWhenNoEligibleUsers = true,

  // Styling
  showLogo = false,

  // Button customization
  backButtonText = 'Startseite',
  backButtonLink = '/',
  authenticateButtonText = 'Authentifizieren',
  showBackButton = true,
  showCloseButton = true,
  showPasskeyLoginButton = false,
  onPasskeyLogin = null,
  passkeyLoginButtonText = 'Mit Passkey anmelden',
  passkeyLoginDisabled = false,

  // Error handling
  error = '',
  setError,
  isProcessing = false,
  setIsProcessing,

  // Animation states
  showCheckmarkAnimation = false,
  setShowCheckmarkAnimation,
  showCrossAnimation = false,
  setShowCrossAnimation,

  // Children for custom content
  children,

  // Custom handlers
  customRfidHandler = null,
  customPasswordHandler = null,

  // Device type handling
  isCounterDevice = true,
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // Image preloading state
  const [imageLoaded, setImageLoaded] = useState(false);

  // Check if images are already preloaded globally
  useEffect(() => {
    const preloadedImage = getPreloadedImage(funcard);
    if (preloadedImage) {
      setImageLoaded(true);
    } else {
      // Fallback: preload locally if not globally available
      const preloadImage = () => {
        const img = new Image();
        img.onload = () => {
          setImageLoaded(true);
        };
        img.onerror = () => {
          console.warn('Failed to preload funcard image');
          setImageLoaded(true); // Continue anyway
        };
        img.src = funcard;
      };

      preloadImage();
    }
  }, []);

  // Reset state when component opens/closes
  useEffect(() => {
    if (isOpen) {
      setError && setError('');
    }
  }, [isOpen, setError]);

  // Handle Enter key for password login
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Enter' && showPasswordLogin) {
        handlePasswordLogin(e);
      }
    };

    if (showPasswordLogin) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPasswordLogin]);

  const handleRfidInput = async input => {
    if (customRfidHandler) {
      return customRfidHandler(input);
    }

    setError && setError('');
    setIsProcessing && setIsProcessing(true);

    try {
      // Hash the input to search for matching intermediate token
      const hashedInput = await hashCardId(input);

      // Check if there's a token for this card hash
      const hasToken = intermediateTokens[hashedInput];

      if (hasToken) {
        // Check if the user from this token is eligible
        const isUserEligible =
          eligibleUsers.length === 0 || eligibleUsers.some(user => user.cardHash === hashedInput);

        if (isUserEligible) {
          // Show checkmark animation
          setShowCheckmarkAnimation && setShowCheckmarkAnimation(true);

          // Wait for animation to finish
          await new Promise(resolve => setTimeout(resolve, 800));

          setShowCheckmarkAnimation && setShowCheckmarkAnimation(false);

          // If successful, call the success callback with card ID and token data
          onRfidSuccess && onRfidSuccess(input, hasToken);
          return;
        } else {
          // Show red cross animation when user is not eligible
          setShowCrossAnimation && setShowCrossAnimation(true);

          // Wait for animation to finish
          await new Promise(resolve => setTimeout(resolve, 800));

          setShowCrossAnimation && setShowCrossAnimation(false);

          // Show error message that this user is not authorized
          setError && setError('Dieser Nutzer ist nicht berechtigt, diese Aktion durchzuführen.');
        }
      } else {
        // Show red cross animation when no matching token found
        setShowCrossAnimation && setShowCrossAnimation(true);

        // Wait for animation to finish
        await new Promise(resolve => setTimeout(resolve, 800));

        setShowCrossAnimation && setShowCrossAnimation(false);

        // Show toast notification and switch to password login
        toast.info(
          'Für deinen ersten Login heute musst du dich mit deinen Anmeldedaten anmelden. Danach kannst du deinen Mitarbeiterausweis für weitere Logins verwenden.'
        );
        setShowPasswordLogin && setShowPasswordLogin(true);
      }
    } catch (err) {
      console.error('Error in handleRfidInput:', err);
      setError && setError(err.message || 'Fehler beim RFID-Login');
      onRfidError && onRfidError(err);
    } finally {
      setIsProcessing && setIsProcessing(false);
    }
  };

  const { ScannerInput } = useCardScanner({
    enabled: isOpen && !showPasswordLogin && isCounterDevice,
    onComplete: handleRfidInput,
    debounceMs: 100,
  });

  const handlePasswordLogin = async event => {
    event.preventDefault();

    if (customPasswordHandler) {
      return customPasswordHandler(event);
    }

    setError && setError('');

    // Get the form element - ensure we have the actual form, not a child element
    const formElement =
      event.target.tagName === 'FORM' ? event.target : event.target.closest('form');

    if (!formElement) {
      // user tried to use card -> use card login
      setShowPasswordLogin && setShowPasswordLogin(false);
      console.log(event);
      return;
    }

    // Get form data from uncontrolled elements
    const formData = new FormData(formElement);
    const username = formData.get('username');
    const password = formData.get('password');

    // Validate that both username and password are provided and not just whitespace
    if (!username || !password || username.trim() === '' || password.trim() === '') {
      setError && setError('Bitte gib sowohl Benutzername als auch Passwort ein.');
      return;
    }

    try {
      // Call success callback with username and password separately
      onPasswordSuccess && onPasswordSuccess(null, username.trim(), password.trim());
    } catch (error) {
      console.error('Error in password authentication:', error);
      setError && setError('Fehler bei der Authentifizierung: ' + error.message);
      onPasswordError && onPasswordError(error);
    }
  };

  const renderLogo = () => {
    if (!showLogo) return null;

    return (
      <Grid xs={12} style={{ marginBottom: '20px' }}>
        <FunZoneLogo />
      </Grid>
    );
  };

  const renderEligibleUsers = () => {
    if (!showNoEligibleUsersMessage) {
      return null;
    }

    // Show message when no users are eligible
    if (eligibleUsers.length === 0 && showNoEligibleUsersMessage) {
      return (
        <Grid xs={12}>
          <Box
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: '6px',
              p: 1.5,
              mb: 0.5,
            }}
          >
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#ffffff', textAlign: 'center' }}
            >
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px' }} />
              Keine berechtigten Personen
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: '#ffffff',
                textAlign: 'center',
                fontSize: '0.8rem',
                display: 'block',
              }}
            >
              Es sind keine Personen mit den erforderlichen Berechtigungen verfügbar.
            </Typography>
          </Box>
        </Grid>
      );
    }

    return (
      <Grid xs={12}>
        <Box
          sx={{
            backgroundColor: isLight ? theme.palette.grey[100] : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            p: 1.5,
            mb: 0.5,
            border: isLight ? `1px solid ${theme.palette.divider}` : '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography
            variant='body2'
            sx={{
              mb: 1.5,
              fontWeight: 600,
              fontSize: '0.9rem',
              color: isLight ? 'text.primary' : 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
            }}
          >
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px' }} />
            Berechtigte Personen
          </Typography>

          {eligibleUsers.length === 1 ? (
            // Single user: display centered
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                sx={{
                  backgroundColor: isLight ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.05)',
                  color: isLight ? 'text.primary' : 'rgba(255, 255, 255, 0.9)',
                  width: '100%',
                  border: isLight ? `1px solid ${theme.palette.divider}` : '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 0.5,
                    '&:last-child': { pb: 0.5 },
                  }}
                >
                  <AssignmentIndIcon size='small' sx={{ fontSize: '0.9rem' }} />
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.85rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {eligibleUsers[0].name}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ) : (
            // Multiple users: display in grid
            <Grid container spacing={0.5}>
              {eligibleUsers.map((user, index) => (
                <Grid xs={6} key={index}>
                  <Card
                    sx={{
                      backgroundColor: isLight ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.05)',
                      color: isLight ? 'text.primary' : 'rgba(255, 255, 255, 0.9)',
                      border: isLight ? `1px solid ${theme.palette.divider}` : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        p: 0.5,
                        '&:last-child': { pb: 0.5 },
                      }}
                    >
                      <AssignmentIndIcon size='small' sx={{ fontSize: '0.8rem' }} />
                      <Typography
                        variant='caption'
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {user.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Grid>
    );
  };

  const renderPasswordFields = () => {
    if (!showPasswordLogin) return null;

    const textFieldStyles = {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: isLight ? theme.palette.grey[400] : 'rgba(255, 255, 255, 0.3)',
        },
        '&:hover fieldset': {
          borderColor: isLight ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.5)',
        },
        '&.Mui-focused fieldset': {
          borderColor: isLight ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.8)',
        },
      },
      '& .MuiInputLabel-root': {
        color: isLight ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.7)',
        '&.Mui-focused': {
          color: isLight ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.9)',
        },
      },
      '& .MuiInputBase-input': {
        color: isLight ? theme.palette.text.primary : 'white',
        '&::placeholder': {
          color: isLight ? theme.palette.text.disabled : 'rgba(255, 255, 255, 0.5)',
          opacity: 1,
        },
      },
    };

    return (
      <>
        <Grid xs={12} sx={{ mt: 2 }}>
          <TextField
            name='username'
            label='Benutzername, E-Mail oder Vor- und Nachname'
            variant='outlined'
            fullWidth
            required
            placeholder='z.B. MaxMustermann1, max@example.com oder Max Mustermann'
            sx={textFieldStyles}
          />
        </Grid>
        <Grid xs={12}>
          <TextField
            name='password'
            label='Passwort'
            variant='outlined'
            type='password'
            fullWidth
            required
            sx={textFieldStyles}
          />
        </Grid>
      </>
    );
  };

  const renderCard = () => {
    if (showPasswordLogin || !isCounterDevice) return null;

    return (
      <Grid xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '12px 0',
            width: '356px',
            height: '230px',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Card Text Overlay - Always render immediately (highest priority) */}
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 2,
              backgroundColor: isLight ? alpha(theme.palette.background.paper, 0.92) : 'rgba(255, 255, 255, 0.5)',
              padding: '8px 16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              // Ensure text is always visible regardless of image loading
              opacity: 1,
              transition: 'none',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: '#00005F',
                fontSize: '0.9rem',
                textAlign: 'center',
                fontWeight: 1000,
                // Force immediate rendering
                willChange: 'auto',
                transform: 'translateZ(0)',
              }}
            >
              {cardText}
            </Typography>
          </Box>

          {/* Loading state */}
          {!imageLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#FFFF00',
                borderRadius: '12px',
                zIndex: 1,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}

          {/* Actual Card Image */}
          <img
            src={funcard}
            alt='FunZone Mitarbeiterausweis'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '12px',
              boxShadow: '0 8px 32px 0 rgba(60,72,100,0.15)',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
            onLoad={() => setImageLoaded(true)}
          />

          {/* Checkmark Animation Overlay */}
          {showCheckmarkAnimation && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(76, 175, 80, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px',
                animation: 'fadeInOut 0.8s ease-in-out',
                '@keyframes fadeInOut': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.8)',
                  },
                  '20%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '80%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  animation: 'checkmarkPulse 0.8s ease-in-out',
                  '@keyframes checkmarkPulse': {
                    '0%': {
                      transform: 'scale(0) rotate(-45deg)',
                    },
                    '30%': {
                      transform: 'scale(1.2) rotate(0deg)',
                    },
                    '70%': {
                      transform: 'scale(1) rotate(0deg)',
                    },
                    '100%': {
                      transform: 'scale(1) rotate(0deg)',
                    },
                  },
                }}
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{
                    fontSize: '4em',
                    color: '#FFFF00',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                  }}
                />
                <Typography
                  variant='h6'
                  sx={{
                    color: '#FFFF00',
                    fontWeight: 600,
                    marginTop: '8px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  Karte erkannt
                </Typography>
              </Box>
            </Box>
          )}

          {/* Cross Animation Overlay */}
          {showCrossAnimation && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px',
                animation: 'fadeInOut 0.8s ease-in-out',
                '@keyframes fadeInOut': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.8)',
                  },
                  '20%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '80%': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  animation: 'crossPulse 0.8s ease-in-out',
                  '@keyframes crossPulse': {
                    '0%': {
                      transform: 'scale(0) rotate(45deg)',
                    },
                    '30%': {
                      transform: 'scale(1.2) rotate(0deg)',
                    },
                    '70%': {
                      transform: 'scale(1) rotate(0deg)',
                    },
                    '100%': {
                      transform: 'scale(1) rotate(0deg)',
                    },
                  },
                }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{
                    fontSize: '4em',
                    color: '#FFFF00',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                  }}
                />
                <Typography
                  variant='h6'
                  sx={{
                    color: '#FFFF00',
                    fontWeight: 600,
                    marginTop: '8px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  Karte nicht erkannt
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const renderErrorAlert = () => {
    if (!error) return null;

    return (
      <Grid xs={12}>
        <Alert severity='error' sx={{ backgroundColor: '#d32f2f', color: '#ffffff' }}>
          {error}
        </Alert>
      </Grid>
    );
  };

  const renderButtons = () => {
    return (
      <Grid xs={12} sx={{ marginTop: '20px' }} container>
        {showBackButton ? (
          <>
            {/* BACK/CANCEL BUTTON */}
            <Grid xs={12} sm={6} sx={{ textAlign: 'left' }}>
              {backButtonLink ? (
                <a href={backButtonLink} style={{ textDecoration: 'none' }}>
                  <Button type='button' sx={{ maxWidth: '200px' }}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
                    {backButtonText}
                  </Button>
                </a>
              ) : (
                <Button type='button' onClick={onCancel || onClose} sx={{ maxWidth: '200px' }}>
                  <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
                  Abbrechen
                </Button>
              )}
            </Grid>

            {/* LOGIN BUTTON - Only show when NOT in password login mode */}
            <Grid xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!showPasswordLogin && (isCounterDevice || (showPasskeyLoginButton && onPasskeyLogin)) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {showPasskeyLoginButton && onPasskeyLogin && (
                    <Button
                      type='button'
                      onClick={onPasskeyLogin}
                      disabled={isProcessing || passkeyLoginDisabled}
                      sx={{ maxWidth: '220px' }}
                    >
                      <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                      {passkeyLoginButtonText}
                    </Button>
                  )}
                  {isCounterDevice && (
                    <Button
                      type='button'
                      onClick={() => setShowPasswordLogin && setShowPasswordLogin(true)}
                      disabled={isProcessing}
                      sx={{ maxWidth: '200px' }}
                    >
                      <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                      Passwort-Login
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          </>
        ) : (
          /* CENTERED LOGIN BUTTON WHEN NO BACK BUTTON - Only show when NOT in password login mode */
          <Grid xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            {!showPasswordLogin && (isCounterDevice || (showPasskeyLoginButton && onPasskeyLogin)) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {showPasskeyLoginButton && onPasskeyLogin && (
                  <Button
                    type='button'
                    onClick={onPasskeyLogin}
                    disabled={isProcessing || passkeyLoginDisabled}
                    sx={{ maxWidth: '220px' }}
                  >
                    <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                    {passkeyLoginButtonText}
                  </Button>
                )}
                {isCounterDevice && (
                  <Button
                    type='button'
                    onClick={() => setShowPasswordLogin && setShowPasswordLogin(true)}
                    disabled={isProcessing}
                    sx={{ maxWidth: '200px' }}
                  >
                    <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                    Passwort-Login
                  </Button>
                )}
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    );
  };

  const renderBackToRfidButton = () => {
    // Don't show this button when not in password login mode
    if (!showPasswordLogin) return null;

    return (
      <Grid xs={12} sx={{ marginTop: '10px' }} container>
        {/* CARD BUTTON - LEFT - Only show for counter devices */}
        {isCounterDevice && (
          <Grid xs={12} sm={6} sx={{ textAlign: 'left' }}>
            <Button
              type='button'
              onClick={() => setShowPasswordLogin && setShowPasswordLogin(false)}
              disabled={isProcessing}
              sx={{ maxWidth: '200px' }}
            >
              <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '10px' }} />
              Karte verwenden
            </Button>
          </Grid>
        )}

        {/* AUTH BUTTON - RIGHT - Show for all devices in password mode */}
        <Grid
          xs={12}
          sm={isCounterDevice ? 6 : 12}
          sx={{ display: 'flex', justifyContent: isCounterDevice ? 'flex-end' : 'center' }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {showPasskeyLoginButton && onPasskeyLogin && (
              <Button
                type='button'
                onClick={onPasskeyLogin}
                disabled={isProcessing || passkeyLoginDisabled}
                sx={{ maxWidth: '220px' }}
              >
                <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                {passkeyLoginButtonText}
              </Button>
            )}
            <Button type='submit' disabled={isProcessing} sx={{ maxWidth: '200px' }}>
              {authenticateButtonText}
              <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '5px' }} />
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const content = (
    <Grid
      xs={12}
      container
      sx={{
        maxWidth: '600px',
        margin: '0 auto',
        justifyContent: 'center',
      }}
      width='95%'
      rowSpacing={1}
    >
      <div style={{ width: '100%' }}>
        {renderLogo()}

        {/* TITLE */}
        {title ? (
          <Grid xs={12}>
            <Typography variant='h5' component='h2' align='center' sx={{ mb: 1, color: 'text.primary' }}>
              {title}
            </Typography>
          </Grid>
        ) : (
          // If no title, render children as custom title
          <Grid xs={12}>{children}</Grid>
        )}

        {/* DESCRIPTION */}
        {description && (
          <Grid xs={12}>
            <Typography variant='body1' align='center' sx={{ mb: 0.5, color: 'text.secondary' }}>
              {description}
            </Typography>
          </Grid>
        )}

        {renderEligibleUsers()}
        {ScannerInput}
        {showPasswordLogin ? (
          <form onSubmit={handlePasswordLogin}>
            {renderPasswordFields()}
            {renderErrorAlert()}
            {renderButtons()}
            {renderBackToRfidButton()}
          </form>
        ) : (
          <>
            {renderCard()}
            {renderErrorAlert()}
            {renderButtons()}
          </>
        )}
        {/* Only render children if there's a title (not when children are used as title) */}
        {title && children}
      </div>
    </Grid>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel || onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '600px',
          width: '95%',
          bgcolor: 'background.paper',
          color: 'text.primary',
          position: 'relative',
        },
      }}
    >
      {/* Close button (X) in top-right corner - only show if showCloseButton is true */}
      {showCloseButton && (
        <IconButton
          onClick={onCancel || onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
            zIndex: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent sx={{ p: 4, bgcolor: 'background.paper' }}>{content}</DialogContent>
    </Dialog>
  );
}

export default CardCheck;
